/* eslint-disable indent */
import React, { useState } from 'react'
import { find, findIndex, get } from 'lodash'
import Joi from 'joi'
import T from 'prop-types'
import useSession from 'hooks/useSession'
import { patchVaccination } from 'lib/requests'
import Message from 'components/Message'
import { useHistory } from 'react-router-dom'
import { getGender } from 'lib/birthNumber'

import getGenderText from '../../helpers/getGenderText'
import Step from '../../components/Step'
import YesOrNo from '../../components/YesOrNo'
import { getOptions as getWomanOptions } from './WomanQuestions'

/**
 * Build the list of clinical questions provided form values
 *
 * requiredValue is the value required to allow vaccination
 */
export const getOptions = (values = {}, user_id = null, showInput = () => {}) => {
  const ssn = String(get(values, 'details.on_behalf_of.national_id', user_id))

  return [
    // Disqualifying questions (+ pregnancy)
    {
      label: getGenderText('Har %s forkjølelsessymptomer eller feber over 38 °C?', values),
      name: 'HAS_SYMPTOMS',
      description:
        'Vaksinering bør utsettes ved akutt infeksjonssykdom med feber over 38 grader. Skulle slik sykdom oppstå mellom nå og vaksinasjonstidspunktet, ber vi deg endre din time.',
      requiredValue: false,
    },
    {
      label: getGenderText('Har %s gjennomgått koronasykdom for mindre enn 3 uker siden?', values),
      name: 'RECENTLY_HAD_COVID',
      requiredValue: false,
      description: 'For ungdom 12-15 år gjelder det uavhengig av tid siden sykdom.',
    },
    {
      label: getGenderText(
        'Har %s blitt vaksinert med annen vaksine i løpet av siste 7 dager?',
        values,
      ),
      name: 'RECENT_FLU_VACCINE',
      requiredValue: false,
    },

    // Non disqualifying questions
    {
      label: (
        <div>
          {getGenderText('Har %s', values)} gjennomgått multiorgan inflammatorisk syndrom (
          <strong>MIS</strong>) etter koronasykdom?
        </div>
      ),
      name: 'HAD_MIS_AFTER_COVID',
    },
    {
      label: (
        <div>
          {getGenderText('Har %s', values)} <strong>mastcellesykdom</strong>?
        </div>
      ),
      name: 'HAS_MAST_CELL_DISEASE',
    },
    {
      label: (
        <div>
          {getGenderText('Har %s', values)} på grunn av <strong>astma</strong> i løpet av det siste
          året fått minst 2 kortisonkurer eller vært innlagt i sykehus?
        </div>
      ),
      name: 'ASTHMA_CORTISONE',
    },
    {
      label: (
        <div>
          <p>
            {getGenderText('Har %s', values)} på grunn av <strong>astma</strong> hatt minst 3 av
            følgende symptomer siste 4 uker:
          </p>
          <ul style={{ marginLeft: '1.5rem' }}>
            <li style={{ listStyle: 'initial' }}>Symptomer på dagtid mer enn 2 ganger per uke.</li>
            <li style={{ listStyle: 'initial' }}>Våknet på natten.</li>
            <li style={{ listStyle: 'initial' }}>
              Behov for anfallsmedisin mer enn 2 ganger per uke.
            </li>
            <li style={{ listStyle: 'initial' }}>Begrensning i fysisk aktivitet.</li>
          </ul>
        </div>
      ),
      name: 'ASTHMA_SYMPTOMS',
    },
    {
      label: (
        <div>
          {getGenderText('Har %s', values)} hatt hjertebetennelse (myokarditt/perikarditt) etter
          koronavaksinasjon?
        </div>
      ),
      name: 'HAD_ECEPHALITIS_AFTER_VACCINATION',
    },
    ...(getGender(ssn) !== 'MALE'
      ? [
          {
            label: (
              <div>
                {getGenderText('Har %s', values)} hatt behandlingstrengende eller vedvarende
                menstruasjonsforstyrrelser etter koronavaksinasjon?
              </div>
            ),
            name: 'HAD_MENSTRUAL_DISTURBANCES_AFTER_VACCINATION',
          },
        ]
      : []),
    {
      label: (
        <div>
          {getGenderText('Har %s', values)} hatt <strong>allergisk reaksjon</strong> på denne
          vaksinen eller innholdsstoffene tidligere?
        </div>
      ),
      name: 'HAS_PREVIOUS_VACCINE_REACTIONS',
    },
    {
      label: (
        <div>
          {getGenderText('Har %s', values)} tidligere hatt alvorlig (livstruende){' '}
          <strong>allergisk reaksjon</strong> på andre vaksiner, mat, medisiner eller annet?
        </div>
      ),
      name: 'HAS_ALLERGIES',
    },
    {
      label: (
        <div>
          {getGenderText('Har %s', values)} <strong>økt blødningstendens</strong>?
        </div>
      ),
      name: 'HAS_BLEEDING_DISORDER',
    },
    {
      label: (
        <div>
          {getGenderText('Har %s', values)} <strong>kraftig redusert immunforsvar</strong>?
        </div>
      ),
      name: 'HAS_REDUCED_IMMUNE_SYSTEM',
    },
    {
      label: (
        <div>
          {getGenderText('Har %s', values)} <strong>gjennomgått koronasykdom</strong> for mer enn 3
          måneder siden?
        </div>
      ),
      name: 'PREVIOUSLY_HAD_COVID',
    },
  ]
}

/**
 * Check if the vaccination should be cancelled. Used for health workers in the
 * self declaration form after a booking has been created by the hospital managers.
 */
function shouldCancelVaccination(data) {
  const questionKeys = ['HAS_SYMPTOMS', 'IN_QUARANTINE', 'RECENTLY_HAD_COVID', 'RECENT_FLU_VACCINE']

  const validator = Joi.object({
    vaccination: Joi.object({
      // the self declaration should contain
      self_declaration: Joi.array().items(
        // ...any one of the specific questions with a falsy answer
        ...questionKeys.map((question) =>
          Joi.object({
            question: Joi.string().valid(question),
            answer: Joi.bool().valid(false).required(),
          }).unknown(true),
        ),
        // ...or some answer for any question NOT in that list
        Joi.object({
          question: Joi.string()
            .required()
            .invalid(...questionKeys),
        }).unknown(true),
      ),
    })
      .required()
      .unknown(),
  })
    .required()
    .unknown(true)

  return !!validator.validate(data).error
}

/**
 * Build a validator that checks that all the clinical questions has been answered
 *
 * @param array options Array of options, where the name property will be used to build a validator
 */
export const buildClinicalQuestionsValidator = (options, enforceRequiredValue = false) =>
  Joi.object({
    vaccination: Joi.object({
      self_declaration: Joi.array()
        .sparse()
        .items(
          ...options.map(({ name, requiredValue }) =>
            Joi.object({
              question: Joi.string().valid(name),
              ...(enforceRequiredValue && typeof requiredValue !== 'undefined'
                ? {
                    answer: Joi.bool().valid(requiredValue).required(),
                  }
                : {}),
            })
              .required()
              .unknown(true),
          ),
          Joi.object({
            question: Joi.string()
              .required()
              .invalid(...options.map(({ name }) => name)),
          }).unknown(true),
        )
        .required(),
    })
      .required()
      .unknown(true),
  })
    .required()
    .unknown(true)

export const validator = (values, userId) =>
  buildClinicalQuestionsValidator([
    ...getOptions(values, userId, () => {}),
    ...(values?.vaccinationId ? getWomanOptions(values, userId) : []),
  ])

export const handleClinicalInformationChange = (values, onChange) => (value) => {
  const { question } = value
  const currentValue = get(values, 'vaccination.self_declaration', [])
  const changedQuestionIndex = findIndex(currentValue, { question })

  onChange(
    'vaccination.self_declaration',
    changedQuestionIndex === -1
      ? [...currentValue, value]
      : [
          ...currentValue.slice(0, changedQuestionIndex),
          value,
          ...currentValue.slice(changedQuestionIndex + 1),
        ],
  )
}

export default function ClinicalInformation({
  changeStep,
  values,
  onChange,
  orgUnit,
  bookingType,
}) {
  const [error, setError] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const history = useHistory()

  const {
    session: { user_id },
  } = useSession()

  const handleChange = handleClinicalInformationChange(values, onChange)

  async function handleSubmit() {
    const cancelVaccination = shouldCancelVaccination({
      vaccination: {
        self_declaration: get(values, 'vaccination.self_declaration') || [],
      },
    })

    // check if one of the clinical questions was answered with yes
    const clinicalWarning = !!(get(values, 'vaccination.self_declaration') || []).find(
      ({ question, answer }) => question !== 'WANTS_VACCINE' && answer,
    )

    setIsSubmitting(true)

    // patch the vaccination
    const response = await patchVaccination(
      values?.vaccinationId,
      {
        // update self declaration
        self_declaration: get(values, 'vaccination.self_declaration') || [],

        // sett cancelled status if applicable
        ...(cancelVaccination ? { vaccination_status: 'CANCELLED' } : {}),
      },
      false,
      orgUnit.slug,
    )

    if (response.error) {
      setIsSubmitting(false)
      if (response.error?.status === 404) {
        return setError(
          `Kunne ikke finne din time for vaksinasjon. Vennligst sjekk at du har brukt riktig lenke. Error: 404 not found (id: ${values?.vaccinationId})`,
        )
      }
      return setError(get(response, 'error.message', 'Noe gikk feil'))
    }

    setTimeout(() => {
      setIsSubmitting(false)
      history.push(
        cancelVaccination
          ? `/${orgUnit.slug}/avslag-klinisk`
          : `/${orgUnit.slug}/bekreftelse?type=${bookingType?.id}&selfDeclarationUpdate=1${
              clinicalWarning ? '&checkGuidelines=1' : ''
            }`,
      )
    }, 2000)
  }

  const showInput = (question) =>
    find(get(values, 'vaccination.self_declaration', []), { question })?.answer

  const disabled = !!validator(values, user_id, values?.vaccinationId).validate(values)?.error

  return (
    <Step
      title="Egenerklæring"
      submitDisabled={disabled || isSubmitting}
      isLoading={isSubmitting}
      {...(values?.vaccinationId
        ? {
            onSubmit: handleSubmit,
            submit: 'Lagre egenerklæring',
          }
        : {
            onSubmit: changeStep,
            submit: 'Gå videre',
          })}
    >
      <YesOrNo
        highlightErrors
        values={get(values, 'vaccination.self_declaration', [])}
        onChange={handleChange}
        options={[
          ...getOptions(values, user_id, showInput),
          ...(values?.vaccinationId ? getWomanOptions(values, user_id) : []),
        ]}
      />

      {error && (
        <Message type="warning" focus>
          {error}
        </Message>
      )}
    </Step>
  )
}

ClinicalInformation.propTypes = {
  changeStep: T.func,
  values: T.object.isRequired,
  onChange: T.func.isRequired,
  orgUnit: T.shape({
    id: T.string.isRequired,
    slug: T.string.isRequired,
  }).isRequired,
  bookingType: T.shape({
    id: T.string.isRequired,
  }).isRequired,
}
